import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, TextField } from '@mui/material';
import { AssignmentModel, AssignmentStatusType } from '../../../../api-service/clients';
import { DeepPartial } from './interfaces';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { AssignmentTitlezone } from './assignment-title-zone';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';

export interface AssignmentLayoutProps {
  isEditmode: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
  status: AssignmentStatusType;
  assignment: DeepPartial<AssignmentModel>;
  onSaveAssignment: () => void;
  completeLabel?: string;
  launchbox?: React.ReactNode;
  isSocialProviderContext?: boolean;

  backgroundInformation: React.ReactNode;
  coordinationDetails: React.ReactNode;
  assessmentInformation: React.ReactNode;
  commissionedIntervention: React.ReactNode;
  statusReports?: React.ReactNode;
  receiptMessage?: React.ReactNode;
}

export function Assignment(props: AssignmentLayoutProps) {
  const {
    isEditmode,
    isReadOnly,
    isLoading,
    assignment,
    launchbox,
    status,
    onSaveAssignment,
    completeLabel,
    isSocialProviderContext,
    backgroundInformation,
    coordinationDetails,
    assessmentInformation,
    commissionedIntervention,
    statusReports,
    receiptMessage,
  } = props;

  return (
    <Uniform
      titleZone={
        <AssignmentTitlezone
          isEditMode={isEditmode}
          isSocialProviderContext={isSocialProviderContext ?? false}
          status={assignment.status}
          startDate={assignment.backgroundInformation?.startDate ? new Date(assignment.backgroundInformation.startDate) : undefined}
          endDate={assignment.backgroundInformation?.endDate ? new Date(assignment.backgroundInformation.endDate) : undefined}
          name={assignment.backgroundInformation?.name}
        />
      }
      isEditMode={!isReadOnly && isEditmode}
      showButtons={!isEditmode}
      onOk={onSaveAssignment}
      labels={completeLabel ? { completeLabel } : undefined}
      onCancel={() => window.close()}
      okButtonIsLoading={isLoading}
      showSpinnerOnNextAndComplete={isLoading}
      launchbox={launchbox}
    >
      <UniformStep stepName="Bestilling">
        <UniformStepContainer>
          <Accordion defaultExpanded>
            <AccordionSummary>Baggrundsoplysninger</AccordionSummary>
            <AccordionDetails>{backgroundInformation}</AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>Koordinering</AccordionSummary>
            <AccordionDetails>{coordinationDetails}</AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>Centrale oplysninger fra udredningen</AccordionSummary>
            <AccordionDetails>{assessmentInformation}</AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>Bestilt indsats (tilbud og ydelser)</AccordionSummary>
            <AccordionDetails>{commissionedIntervention}</AccordionDetails>
          </Accordion>
          {receiptMessage && (
            <Accordion defaultExpanded>
              <AccordionSummary>Besked til myndighed</AccordionSummary>
              <AccordionDetails>{receiptMessage}</AccordionDetails>
            </Accordion>
          )}
          {statusReports && (
            <Accordion defaultExpanded>
              <AccordionSummary>Status notater</AccordionSummary>
              <AccordionDetails>{statusReports}</AccordionDetails>
            </Accordion>
          )}
          {status === AssignmentStatusType.Historic && assignment.closingRemarks && (
            <Accordion defaultExpanded>
              <AccordionSummary>Afsluttende bemærkning</AccordionSummary>
              <AccordionDetails>
                <Row>
                  <Cell size="full">
                    <TextField
                      label="Bemærkning til afslutning af bestilling"
                      value={assignment.closingRemarks || ''}
                      variant="outlined"
                      multiline
                      rows={4}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Cell>
                </Row>
              </AccordionDetails>
            </Accordion>
          )}
        </UniformStepContainer>
      </UniformStep>
    </Uniform>
  );
}
